import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

import { useUnreadNotificationCount } from '../../hooks/useUnreadNotificationCount';
import { getNotificationFeed, markNotificationRead } from '../../services/NotificationCenterService.jsx';
import { LANDING_PATH } from '../../config/accessControl.js';

import './NotificationBell.css';

const isAccessApproved = (n) => !!n && n.useCaseId === 'RB4' && /\bapprov/i.test(n.body || '');

function relativeTime(iso) {
    if (!iso) return '';
    const then = typeof iso === 'number' ? iso : Date.parse(iso);
    if (Number.isNaN(then)) return '';
    const sec = Math.round((Date.now() - then) / 1000);
    if (sec < 60) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.round(hr / 24)}d ago`;
}

export const NotificationBell = () => {
    const navigate = useNavigate();
    const unread = useUnreadNotificationCount();
    const hasUnread = unread > 0;
    const label = hasUnread ? `Notifications (${unread} unread)` : 'Notifications';

    // Ringing nudge while something is unread — stops once the user opens the
    // bell once. A fresh arrival (count rises) re-arms it.
    const [animate, setAnimate] = useState(false);
    const prevUnreadRef = useRef(0);
    useEffect(() => {
        if (unread > prevUnreadRef.current) setAnimate(true);
        if (unread === 0) setAnimate(false);
        prevUnreadRef.current = unread;
    }, [unread]);

    // The bell opens an in-place DROPDOWN PANEL — it does NOT navigate to the
    // /notifications route. That route mounts its own DashboardLayout, so
    // navigating to it remounts the whole shell (which read as the page
    // "refreshing" and surfaced newly-granted access unexpectedly). A panel
    // keeps the user exactly where they are; any real action (open a deep link,
    // refresh to load new access, view-all) is then an explicit click.
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const btnRef = useRef(null);
    const panelRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, right: 0 });

    const loadFeed = useCallback(async () => {
        setLoading(true);
        try {
            const feed = await getNotificationFeed({ limit: 8 });
            const list = Array.isArray(feed) ? feed : (feed?.items || feed?.notifications || []);
            setItems(list);
        } catch {
            // leave the previous list; the panel still shows "View all"
        } finally {
            setLoading(false);
        }
    }, []);

    const openPanel = () => {
        setAnimate(false); // opening the bell IS the acknowledgement
        if (btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
        }
        setOpen(true);
        loadFeed();
    };

    const closePanel = useCallback(() => setOpen(false), []);

    // Close on outside click / Escape.
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            closePanel();
        };
        const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, closePanel]);

    // Per-notification action — the ONLY thing that performs navigation/refresh,
    // and only on an explicit click.
    const handleAction = async (n) => {
        if (isAccessApproved(n)) {
            // Permissions changed server-side but this SPA is stale — hard reload
            // into Home to re-bootstrap the new access. Await the mark-read first:
            // the reload would otherwise abort the in-flight request and the
            // notification would resurface as unread after the page came back.
            if (!n.read) {
                try { await markNotificationRead(n.id); } catch { /* reload anyway */ }
            }
            window.location.assign(LANDING_PATH);
            return;
        }
        if (!n.read) { markNotificationRead(n.id).catch(() => {}); }
        if (n.deepLink) {
            closePanel();
            if (/^https?:\/\//i.test(n.deepLink)) window.open(n.deepLink, '_blank', 'noopener,noreferrer');
            else navigate(n.deepLink);
        }
    };

    const panel = open ? createPortal(
        <div
            ref={panelRef}
            className="nb-panel"
            style={{ position: 'fixed', top: `${pos.top}px`, right: `${pos.right}px` }}
            role="dialog"
            aria-label="Notifications"
        >
            <div className="nb-panel-header">
                <span className="nb-panel-title">Notifications</span>
                {hasUnread && <span className="nb-panel-count">{unread} unread</span>}
            </div>

            <div className="nb-panel-list">
                {loading && items.length === 0 ? (
                    <div className="nb-panel-empty">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="nb-panel-empty">You&apos;re all caught up</div>
                ) : (
                    items.map((n) => {
                        const approved = isAccessApproved(n);
                        return (
                            <div key={n.id} className={`nb-item ${n.read ? '' : 'nb-item-unread'}`}>
                                <div className="nb-item-main">
                                    {n.title && <div className="nb-item-title">{n.title}</div>}
                                    {n.body && <div className="nb-item-body">{n.body}</div>}
                                    <div className="nb-item-time">{relativeTime(n.createdAt)}</div>
                                </div>
                                {(approved || n.deepLink) && (
                                    <button
                                        type="button"
                                        // Blue (primary) while the notification is still unread — i.e. not yet
                                        // acted on; greys out once read. The CTA click marks it read (see
                                        // handleAction), so reviewing a request flips its button to grey.
                                        className={`nb-item-cta ${n.read ? '' : 'nb-item-cta-primary'}`}
                                        onClick={() => handleAction(n)}
                                    >
                                        {approved ? 'Refresh to load access' : (n.ctaLabel || 'View')}
                                    </button>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <button
                type="button"
                className="nb-panel-viewall"
                onClick={() => { closePanel(); navigate('/notifications'); }}
            >
                View all notifications
            </button>
        </div>,
        document.body,
    ) : null;

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={`header-notif-btn ${animate ? 'header-notif-btn--animate' : ''}`}
                onClick={() => (open ? closePanel() : openPanel())}
                aria-label={label}
                aria-expanded={open}
                title={label}
            >
                <FontAwesomeIcon icon={faBell} className="header-notif-icon" />
                {hasUnread && (
                    <span className="header-notif-badge" aria-hidden="true">
                        {unread > 99 ? '99+' : unread}
                    </span>
                )}
            </button>
            {panel}
        </>
    );
};

export default NotificationBell;
